// Listeners are attached once at app boot (this module is imported for its side effects)
// so signals accumulate across the whole session, not just around a single action.

let pointerMoveCount = 0
let keydownTimestamps = []
let lastTrustedInteractionAt = null

// Markers left behind by common headless/automation tooling
const AUTOMATION_GLOBAL_KEYS = [
    "__nightmare",
    "_phantom",
    "callPhantom",
    "__selenium_unwrapped",
    "__selenium_evaluate",
    "__webdriver_evaluate",
    "__driver_evaluate",
    "__fxdriver_unwrapped",
    "domAutomation",
    "domAutomationController",
    "Cypress",
]

// No genuine pointer/key event in this window before an action is treated as suspicious
const ACTION_TIMING_THRESHOLD_MS = 10_000

function markTrustedInteraction(e) {
    if (e.isTrusted) lastTrustedInteractionAt = Date.now()
}

if (typeof window !== "undefined") {
    // Pointer Events unify mouse, touch-drag and pen under one listener, so this tracks movement
    // consistently across desktop and touch devices (a plain "mousemove" listener never fires on
    // a touchscreen, which would otherwise make every genuine mobile user look suspicious below).
    window.addEventListener("pointermove", (e) => {
        pointerMoveCount += 1
        markTrustedInteraction(e)
    }, {passive: true})

    window.addEventListener("keydown", (e) => {
        keydownTimestamps.push(Date.now())
        markTrustedInteraction(e)
    }, {passive: true})

    window.addEventListener("pointerdown", markTrustedInteraction, {passive: true})
}

function hasAutomationGlobals() {
    const globalHit = AUTOMATION_GLOBAL_KEYS.some(key => key in window)
    // chromedriver injects a randomized "$cdc_..." property on document
    const cdcHit = Object.keys(document).some(key => key.startsWith("$cdc_") || key.startsWith("$wdc_"))
    return globalHit || cdcHit
}

function getKeystrokeIntervalVariance() {
    if (keydownTimestamps.length < 3) return null

    const intervals = keydownTimestamps.slice(1).map((t, i) => t - keydownTimestamps[i])
    const mean = intervals.reduce((sum, v) => sum + v, 0) / intervals.length
    return intervals.reduce((sum, v) => sum + (v - mean) ** 2, 0) / intervals.length
}

// getAiAgentSignals()
// -------------------
// Produces a lightweight heuristic score estimating whether the current session is being
// driven by an automation/AI agent rather than a human, for inclusion in the device
// fingerprint payload (see fingerprintConfig.js, spread into the fingerprint result).
//
// This is NOT a fingerprint identifier — it's a fraud/bot-risk signal. It's cheap, runs
// entirely client-side, and is meant to be combined with other signals (botd, webdriver
// checks, etc.) rather than trusted alone. A sophisticated agent driving a real browser
// with randomized timing and synthetic mouse movement can defeat every check below.
//
// Score is built from five independent 0.2-weighted signals (summing to 1.0 if all trip),
// each targeting a different tell:
//
//   1. navigator.webdriver (+0.2)
//      Set to `true` by browsers when launched under WebDriver (Selenium, Playwright,
//      Puppeteer's default profile, etc.). Cheapest and most reliable signal, but easily
//      spoofed/patched out by anti-detection tooling.
//
//   2. Automation globals / CDC properties (+0.2, hasAutomationGlobals())
//      Older automation frameworks (PhantomJS, Nightmare, old Selenium/ChromeDriver,
//      Cypress) leave marker properties on `window`/`document`. ChromeDriver in particular
//      injects a randomized "$cdc_..." property on `document`, so we scan key prefixes
//      rather than a fixed name.
//
//   3. Zero pointer movement (+0.2, pointerMoveCount === 0)
//      Listeners attached once at app boot (module side effect, not per-page) tally
//      pointermove events for the whole session - this covers mouse movement, touch drags/
//      scrolls, and pen input alike, so touch-only mobile users aren't penalized the way a
//      mousemove-only check would. An agent driving via keyboard/DOM APIs only, or issuing
//      actions before any pointer activity, never increments this.
//
//   4. Keystroke timing (+0.2, getKeystrokeIntervalVariance())
//      Human typing has irregular gaps between keydown events; scripted `sendKeys`-style
//      input tends to fire at near-identical intervals. We compute the variance of the
//      last N keydown deltas and flag anything under a 2ms^2 threshold as scripted.
//      Needs at least 3 keydown events to produce a value — otherwise this signal is
//      skipped (not scored as suspicious).
//
//   5. Stale trusted interaction (+0.2, timeSinceTrustedInteraction)
//      `markTrustedInteraction` only records a timestamp when `event.isTrusted` is true —
//      i.e. the event came from real hardware/OS input, not `dispatchEvent()`. If more
//      than ACTION_TIMING_THRESHOLD_MS (10s) has elapsed since the last trusted event (or
//      none has ever occurred), that's consistent with an agent calling APIs directly
//      instead of a human interacting with the page.
//
// Score is clamped to [0, 1] and rounded to one decimal. `isAiAgent` is a simple threshold:
// true once score >= 0.5 (i.e. at least 3 of the 5 signals tripped). Treat both fields as a
// risk score to log/gate on, not a hard verdict.
export function getAiAgentSignals() {
    let score = 0

    if (typeof navigator !== "undefined" && navigator.webdriver) score += 0.2
    if (hasAutomationGlobals()) score += 0.2
    if (pointerMoveCount === 0) score += 0.2

    const variance = getKeystrokeIntervalVariance()
    if (variance !== null && variance < 2) score += 0.2 // near-identical gaps between keystrokes read as scripted input

    const timeSinceTrustedInteraction = lastTrustedInteractionAt === null
        ? Infinity
        : Date.now() - lastTrustedInteractionAt
    if (timeSinceTrustedInteraction > ACTION_TIMING_THRESHOLD_MS) score += 0.2

    score = Math.round(Math.min(score, 1) * 10) / 10

    return {
        isAiAgent: score >= 0.5,
        aiAgentScore: score,
    }
}
